import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Trophy, Star, Clock, Users, ArrowLeft, Zap, Crown, Medal, Gift, ChevronRight, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL.replace(/^http:\/\//i, 'https://');

const WeeklyJackpotPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  const token = localStorage.getItem('token');

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statusRes, lbRes, histRes] = await Promise.all([
        fetch(`${API_URL}/api/weekly-jackpot/status`, { headers }),
        fetch(`${API_URL}/api/weekly-jackpot/leaderboard`),
        fetch(`${API_URL}/api/weekly-jackpot/history`),
      ]);

      if (statusRes.ok) {
        const s = await statusRes.text();
        try { setStatus(JSON.parse(s)); } catch { /* skip */ }
      }
      if (lbRes.ok) {
        const l = await lbRes.text();
        try { setLeaderboard(JSON.parse(l).leaderboard || []); } catch { /* skip */ }
      }
      if (histRes.ok) {
        const h = await histRes.text();
        try { setHistory(JSON.parse(h).history || []); } catch { /* skip */ }
      }
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRegister = async () => {
    setRegistering(true);
    try {
      const res = await fetch(`${API_URL}/api/weekly-jackpot/register`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: 'Fehler' }; }
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      toast.success(data.message || 'Registriert!');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRegistering(false);
    }
  };

  const progress = status ? Math.min((status.tournaments_played / status.threshold) * 100, 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="animate-pulse text-[#FFD700] text-lg">Jackpot wird geladen...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212]">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white" data-testid="jackpot-back-btn">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#FFD700]" />
            <h1 className="text-xl font-bold text-[#FFD700]" data-testid="jackpot-title">Weekly Jackpot</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Hero Card */}
        <Card className="bg-gradient-to-br from-[#1E1E1E] to-[#2A1F00] border-[#FFD700]/30 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFD700]/5 rounded-full -mr-10 -mt-10" />
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#FFD700]/10 flex items-center justify-center">
                <Crown className="w-6 h-6 text-[#FFD700]" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg" data-testid="jackpot-week-label">
                  Woche {status?.week_id || '...'}
                </h2>
                <p className="text-gray-400 text-sm">Spiele {status?.threshold || 5} Daily Tournaments um teilzunehmen</p>
              </div>
            </div>

            {/* Progress */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Fortschritt</span>
                <span className="text-[#FFD700] font-mono font-bold" data-testid="jackpot-progress-text">
                  {status?.tournaments_played || 0} / {status?.threshold || 5}
                </span>
              </div>
              <div className="w-full h-3 bg-[#121212] rounded-full overflow-hidden">
                <div
                  data-testid="jackpot-progress-bar"
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${progress}%`,
                    background: progress >= 100
                      ? 'linear-gradient(90deg, #FFD700, #FFA500)'
                      : 'linear-gradient(90deg, #444, #666)',
                  }}
                />
              </div>
            </div>

            {/* Status */}
            {status?.qualified && !status?.registered && (
              <Button
                data-testid="jackpot-register-btn"
                onClick={handleRegister}
                disabled={registering}
                className="w-full bg-[#FFD700] hover:bg-[#FFA500] text-black font-bold py-3"
              >
                <Zap className="w-4 h-4 mr-2" />
                {registering ? 'Registriere...' : 'Fuer Jackpot registrieren!'}
              </Button>
            )}
            {status?.registered && (
              <div data-testid="jackpot-registered-badge" className="text-center py-3 bg-[#FFD700]/10 rounded-lg border border-[#FFD700]/30">
                <span className="text-[#FFD700] font-bold flex items-center justify-center gap-2">
                  <Star className="w-4 h-4" /> Du bist registriert!
                </span>
              </div>
            )}
            {!status?.qualified && (
              <div className="text-center py-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 text-sm flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" />
                  Noch {(status?.threshold || 5) - (status?.tournaments_played || 0)} Turniere noetig
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prizes */}
        <Card className="bg-[#1E1E1E] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#FFD700]" /> Preise
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { rank: 1, icon: Crown, color: '#FFD700', label: '1. Platz' },
                { rank: 2, icon: Medal, color: '#C0C0C0', label: '2. Platz' },
                { rank: 3, icon: Medal, color: '#CD7F32', label: '3. Platz' },
                { rank: 4, icon: Star, color: '#666', label: '4. Platz' },
                { rank: 5, icon: Star, color: '#555', label: '5. Platz' },
              ].map(({ rank, icon: Icon, color, label }) => (
                <div key={rank} data-testid={`jackpot-prize-${rank}`} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg border border-white/5">
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5" style={{ color }} />
                    <span className="text-white font-medium">{label}</span>
                  </div>
                  <div className="text-right text-sm">
                    <span className="text-[#FFD700] font-mono font-bold">
                      +{status?.prizes?.xp?.[rank - 1] || 0} XP
                    </span>
                    {(status?.prizes?.premium_days?.[rank - 1] || 0) > 0 && (
                      <span className="text-[#00BFA6] ml-2 font-mono">
                        +{status.prizes.premium_days[rank - 1]}d Premium
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card className="bg-[#1E1E1E] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#00BFA6]" />
              Aktuelle Rangliste
              <span className="text-gray-500 text-sm font-normal ml-auto">
                {status?.total_participants || 0} Teilnehmer
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Noch keine Teilnehmer diese Woche</div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((p, i) => (
                  <div key={i} data-testid={`jackpot-lb-${i}`} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-[#FFD700]/20 text-[#FFD700]' :
                        i === 1 ? 'bg-[#C0C0C0]/20 text-[#C0C0C0]' :
                        i === 2 ? 'bg-[#CD7F32]/20 text-[#CD7F32]' :
                        'bg-white/10 text-gray-400'
                      }`}>
                        {p.rank}
                      </span>
                      <span className="text-white text-sm">{p.email}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-400">{p.tournaments_played} Turniere</span>
                      <span className="text-[#00BFA6] font-mono font-bold">{p.total_score} Pkt</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Previous Results */}
        {status?.previous_results && status.previous_results.winners?.length > 0 && (
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                Letzte Woche: {status.previous_results.week_id}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {status.previous_results.winners.map((w, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-[#FFD700] font-bold">#{w.rank}</span>
                      <span className="text-white">{w.email}</span>
                    </div>
                    <span className="text-[#FFD700] font-mono">+{w.xp_prize} XP</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* History */}
        {history.length > 0 && (
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-white">Vergangene Jackpots</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {history.map((h, i) => (
                  <div key={i} data-testid={`jackpot-history-${i}`} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg text-sm">
                    <span className="text-white font-medium">{h.week_id}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-400">{h.winners?.length || 0} Gewinner</span>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Daily Tournament Link */}
        <Button
          data-testid="go-to-daily-tournament-btn"
          onClick={() => navigate('/daily-tournament')}
          variant="outline"
          className="w-full border-[#00BFA6]/30 text-[#00BFA6] hover:bg-[#00BFA6]/10 py-3"
        >
          <Zap className="w-4 h-4 mr-2" /> Zum Daily Tournament
        </Button>
      </div>
    </div>
  );
};

export default WeeklyJackpotPage;
